import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import hubLayout from 'styles/css/hubLayout.module.css';
import { Contract, ContractProductCount, Jisa, Model, Recorder, RecorderEvents } from 'type/type';
import { useModal } from 'components/common/ModalLayout';
import { deleteRecorderByIdx, getContractByNo, getJisaAll, getModelAll, getModelByName, getRecorderByIdx, getRecorderDuplicateCheck, getRecorderHealthByMac, getRecorderList, getUserContractByContractNo, maxLengthCheck, prevCreateRecorder, replaceRecorder } from 'api/api';
import Loading from 'components/common/Loading';
import { qrScan, sampleVideo } from 'api/bridge';
import { AgentLoginInfoContext } from 'pages/Layout';
import { contractStatusLabel, eventLabels } from 'pages/utils/commonUtils';

function ProductEdit() {
  const { agentLoginInfo } = useContext(AgentLoginInfoContext);
  const navigate = useNavigate();
  const { showModal } = useModal();
  const { recorderBefIdx, contractNo, mac, recorderIdx } = useParams();
  const [loading, setLoading] = useState(true);
  const [isInIp, setIsInIp] = useState<boolean>(false);
  const [originMac, setOriginMac] = useState<string>('');
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const [isPrevCreate, setIsPrevCreate] = useState<boolean>(mac ? true : false);
  const [isEvents, setIsEvents] = useState<boolean>(false);
  const [modelOne, setModelOne] = useState<Model|null>(null);
  const [contract, setContract] = useState<Contract>(Object);

  // useForm
  const { register, formState: { errors, isValid }, setValue } = useForm<any>({
    mode: 'all',
  });
  //2024-11-21
  const [recorder, setRecorder] = useState<Recorder>(Object);
  const [recorderEvents, setRecorderEvents] = useState<RecorderEvents>(Object);
  const [jisa, setJisa] = useState<Jisa[]>([]);


  // 녹화기 PUSH 이벤트 설정
  const handlePushChange = (event: any) => {
    let { name, value } = event.target;
    name = name.replaceAll("_push", "");
  
    setRecorderEvents((prevData) => {
      const { idx, user_id, recorder_mac, ...rest } = prevData; // idx, user_id, recorder_mac를 분리
  
      // name에 해당하는 값이 { push: number; monitor: number } 객체인지 확인
      const currentData = prevData[name as keyof Omit<RecorderEvents, 'idx' | 'user_id' | 'recorder_mac'>];

      // currentData가 객체이고 monitor가 존재할 때만 monitor에 접근
      const currentMonitor = (currentData && typeof currentData === 'object' && 'monitor' in currentData)
        ? currentData.monitor
        : 0;
  
      return {
        idx, // 기존 idx 값 유지
        user_id, // 기존 user_id 값 유지
        recorder_mac, // 기존 recorder_mac 값 유지
        ...rest, // 나머지 필드는 업데이트
        [name as keyof Omit<RecorderEvents, 'idx' | 'user_id' | 'recorder_mac'>]: {
          push: Number(value), // push 값만 업데이트
          monitor: currentMonitor, // monitor 값은 변경되지 않음
        },
      };
    });
  };

  // 녹화기 MONITOR 이벤트 설정
  const handleMonitorChange = (event: any) => {
    let { name, value } = event.target;
    name = name.replaceAll("_monitor", "");
    setRecorderEvents((prevData) => {
      const { idx, user_id, recorder_mac, ...rest } = prevData; // idx, user_id, recorder_mac를 분리
  
      // name에 해당하는 값이 { push: number; monitor: number } 객체인지 확인
      const currentData = prevData[name as keyof Omit<RecorderEvents, 'idx' | 'user_id' | 'recorder_mac'>];

      // currentData가 객체이고 push가 존재할 때만 push에 접근
      const currentPush = (currentData && typeof currentData === 'object' && 'push' in currentData)
        ? currentData.push
        : 0;

      return {
        idx, // 기존 idx 값 유지
        user_id, // 기존 user_id 값 유지
        recorder_mac, // 기존 recorder_mac 값 유지
        ...rest, // 나머지 필드는 업데이트
        [name as keyof Omit<RecorderEvents, 'idx' | 'user_id' | 'recorder_mac'>]: {
          push: currentPush, // push 값은 변경되지 않음
          monitor: Number(value), // monitor 값만 업데이트
        },
      };
    });
  };
  
  const eventsSetting = async () => {
    if (!recorder.model) return showModal('alert', '녹화기 Model이 존재하지 않습니다.');
    const modelRes = await getModelByName(recorder.model);
    if (modelRes.code !== 200) return;
    setModelOne(modelRes.data);

    // 이벤트 값이 1인 항목만 필터링
    const filteredEvents = Object.entries(eventLabels).filter(([key]) => {
      const eventValue = modelRes.data[key];
      return eventValue === 1;
    });

    // { eventKey: { push: 0, monitor: 0 } } 형태로 변환
    const eventObj = filteredEvents.reduce((acc, [key]) => {
      acc[key] = { push: 0, monitor: 0 };
      return acc;
    }, {} as Record<string, { push: number; monitor: number }>);

    setRecorderEvents((prevData) => ({
      ...prevData,
      ...eventObj,
    }));

    setIsEvents(prev => !prev);
  }

  const getJisa = async () => {
    try {
      const response = await getJisaAll(0, 0); // 데이터 가져오는 함수
      if (response.code === 200) {
        // `jisa_code`를 string[]로 변환
        const jisaCodeList = Array.isArray(agentLoginInfo.jisa_code)
          ? (agentLoginInfo.jisa_code as string[])
          : [];
    
        // `agentLoginInfo.jisa_code`에 포함된 `jisa_code`만 필터링
        const filteredJisa = response.data.filter((jisa: Jisa) =>
          jisaCodeList.includes(jisa.jisa_code)
        );
    
        setJisa(filteredJisa);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // 계약 onChange 이벤트
  const handleChange = (event: any) => {
    const { name, value } = event.target;
    // console.log(name, value);
    if (name === "mac") {
      setRecorder((prevData) => ({
        ...prevData,
        [name]: value.toUpperCase(),
      }));
    }
    else if (name.includes('in_ip')) {
      let val: string;
      const inIp1 = recorder?.in_ip?.split('.')[0] || "";
      const inIp2 = recorder?.in_ip?.split('.')[1] || "";
      const inIp3 = recorder?.in_ip?.split('.')[2] || "";
      const inIp4 = recorder?.in_ip?.split('.')[3] || "";
      if (name === "in_ip1") { val = value + '.' + inIp2 + '.' + inIp3 + '.' + inIp4 }
      else if (name === "in_ip2") { val = inIp1 + '.' + value + '.' + inIp3 + '.' + inIp4 }
      else if (name === "in_ip3") { val = inIp1 + '.' + inIp2 + '.' + value + '.' + inIp4 }
      else if (name === "in_ip4") { val = inIp1 + '.' + inIp2 + '.' + inIp3 + '.' + value }
      setRecorder((prevData) => ({
        ...prevData,
        'in_ip': val,
      }));
    }
    else {
      setRecorder((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
    if (name === "connect_type") {
      value === 'IP' ? setIsInIp(true) : setIsInIp(false);
    }
  }

  const _replaceRecorder = async () => {
    setLoading(true);
    try {
      recorder.manager_id = agentLoginInfo.id;
      recorder.recorder_events = recorderEvents;
      const recorderRes = await replaceRecorder(recorder);
      if (recorderRes.code === 200) {
        showModal('alert','장비교체가 완료되었습니다.',  {confirm: false, cancel: false},
        () => {
          navigate(-1);
        },
        () => {
          navigate(-1);
        },
        );
      } else if (recorderRes.code === 411) {
        showModal('alert', '이미 사용중인 MAC입니다.');
      } else {
        showModal('alert', recorderRes.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  function handleToNumericValue(event: any) {
    const input = event.target.value;
    // 숫자만 추출
    const numericValue = input.replace(/[^0-9]/g, "");
    event.target.value = numericValue;
    handleChange(event);
  }

  // `qrScanner` 호출 => 완료시 qrCallback
  const qrScanner = () => {
    try {
      qrScan();
    } catch (error) {
      console.log(error)
    }
  };
  // qrCallback을
  window.qrCallback = (response: any) => {
    try {
      handleChange({ target : { name : 'mac', value : response } });
      setValue('mac', response, { shouldValidate: true });
    } catch (error) {
      showModal('alert', `일치하지 않는 형식입니다.(${response})`);
    }
  };

  const prevHealtDelete = () => {
    setLoading(true);
    try {
      showModal('confirm', '1차 등록을 삭제하시겠습니까?', { confirm: false, cancel: false }, 
        async () => {
          const deletePrevRes = await deleteRecorderByIdx(recorder.idx);
          if (deletePrevRes.code !== 200) {
            return showModal('alert', deletePrevRes.message);
          }
          setIsPrevCreate(false);
          setIsSearch(false);
          showModal('alert', '1차 등록을 삭제 처리하였습니다. 다시 개통을 시작해주세요.');
        }
      );
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  const prevHealthCreate = async () => {
    setLoading(true);
    try {
      if (originMac === recorder.mac) {
        showModal('alert', '기존 MAC과 동일합니다.');
        return;
      }
      // 계약번호 확인
      const contractRes = await getContractByNo(recorder.contract_no);
      if (contractRes.code !== 200) return showModal('alert', '미등록 계약번호입니다.');
      if (!(contractRes.data.contract_status === 'B' || contractRes.data.contract_status === 'C')) {
        return showModal('alert', `${contractStatusLabel.find(item => item.code === contractRes.data.contract_status)?.name}상태의 계약입니다. 해당 계약번호의 상태를 변경 후 다시 등록해주세요.`);
      } 
    
      // 사용자 계약 정보 확인
      const usersResponse = await getUserContractByContractNo(recorder.contract_no);
      if (usersResponse.code !== 200) return showModal('alert', usersResponse.message);
    
      if (usersResponse.data?.user_id !== recorder.user_id) {
        return showModal('alert', 'ID와 계약번호 매칭 후 장비등록해주세요.');
      }
    
      // 중복 MAC 체크
      const duplResponse = await getRecorderDuplicateCheck(recorder.mac);
      if (duplResponse.code === 200) {
        const recorderListRes = await getRecorderList(0, 0, { mac: recorder.mac, is_registe: 'All' });
        const recorderListData = recorderListRes.data;
      
        const unregisteredRecorder = recorderListData.find((rec: Recorder) => !rec.is_registe);
      
        if (unregisteredRecorder) {
          return showModal('confirm', `다른 계정에 이미 1차 등록 된 MAC입니다. 삭제 후 진행이 가능합니다. 삭제하시겠습니까?`, { confirm: false, cancel: false },
            async () => {
              const delRes = await deleteRecorderByIdx(unregisteredRecorder.idx);
              if (delRes.code === 200) {
                showModal('alert', '삭제가 완료되었습니다. 개통을 다시 시작해주세요.');
              }
            }
          );
        }
      
        if (recorderListData.length > 0) return showModal('alert', '이미 사용중인 MAC입니다.');
      }
    
      // 1차 등록 진행
      const prevCreateRes = await prevCreateRecorder(recorder);
      // 헬스서버에만 장비가 있을경우 프로세스 필요
      if (prevCreateRes.code !== 200) {
        return showModal('alert', prevCreateRes.message);
      }
      showModal(
        'alert',
        `1차 등록이 완료되었습니다.
        1. 녹화기 네트워크 포트포워딩 설정
        2. Cloud 메뉴 Health "수동업데이트"
        3. 서비스 상태 : "개통 중"
        확인 후 Health조회 버튼을 클릭하여 등록을 이어서 진행해 주세요.\n
        만약 다른 장비로 등록 시 '1차 등록 삭제' 후 등록해주세요.`
      );
      
      setRecorder((prevData) => ({
        ...prevData,
        idx: prevCreateRes.data[0].idx,
        user_id: prevCreateRes.data[0].user_id,
        bef_idx: Number(recorderBefIdx),
      }));
      setRecorderEvents((prevData) => ({
        ...prevData,
        user_id: prevCreateRes.data[0].user_id,
        recorder_mac: recorder.mac,
      }));
      setIsPrevCreate(true);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const searchHealth = async () => {
    setLoading(true);
    try {
      const healthResponse = await getRecorderHealthByMac(recorder.mac);
      const healthData = healthResponse.data;

      if (healthResponse.code !== 200 || !healthData?.rid) {
        return showModal("alert", "해당 장비의 네트워크 상태를 확인해주세요.");
      }
    
      const { dvr_date, dvr_time, out_ip, tcp_port, web_port, fw, model, in_ip, rid, rpw, ch } = healthData;
    
      const contractRes = await getContractByNo(recorder.contract_no);
      if (contractRes.code !== 200) return showModal('alert', '미등록 계약번호입니다.');
      const contractOne = contractRes.data;
    
      const contractProductCount: ContractProductCount | undefined = contractOne.contract_product_count;

      if (!contractProductCount?.recorder?.length) return showModal("alert", "해당 계약에 등록 가능한 녹화기가 없습니다.");

      const matched = contractProductCount.recorder.find(r => r.model === model);
      
      if (!matched) {
        return showModal("alert", "해당 계약에 등록 가능한 녹화기 Model이 아닙니다.");
      }
      
      const { code, data } = await getRecorderList(0, 0, { model, contract_no: contractOne.contract_no });
      if (code === 200) {
        const possibilityCnt = matched.use - data.length;
      
        if (possibilityCnt <= 0) {
          return showModal("alert", "해당 계약에 등록 가능한 녹화기 Model의 수가 많습니다.");
        }
      }
    
      // 날짜 및 시간 변환
      const formatDateTime = (date: string, time: string) => 
        `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)} ` +
        `${time.slice(0, 2)}:${time.slice(2, 4)}:${time.slice(4, 6)}`;
    
      setContract(contractOne);
      setRecorder((prevData) => ({
        ...prevData,
        ip: out_ip,
        tcp_port,
        web_port,
        uptime: formatDateTime(dvr_date, dvr_time),
        fw_ver: fw,
        model,
        vendor: recorder.mac.startsWith('90DA6A') ? 'FOCUS' : '',
        in_ip,
        in_port: tcp_port,
        id: rid,
        password: rpw,
        ch,
      }));
    
      setIsSearch(true);
      showModal('alert','Health정보 조회가 완료되었습니다. 확인 버튼 클릭 시 샘플 영상보기가 실행됩니다.', { confirm: false, cancel: false },
        () => {
          getSampleVideo(healthData);
        },
      );
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getSampleVideo = (healthData: any) => {
    const { mac, out_ip, tcp_port, model, rid, rpw } = healthData;
    sampleVideo(mac, out_ip, tcp_port, rid, rpw, 'Sample Video', 'DDNS', model, "Normal");
  };

  const getRecorder = useCallback(async () => {
    setLoading(true);
    try {
      await getJisa();
      await getModel();
      const response = await getRecorderByIdx(Number(recorderBefIdx));
      if (response.code === 200) {
        setRecorder({
          ...response.data,
          reg_type: "replace",
          idx: recorderIdx ? Number(recorderIdx) : response.data.idx,
          mac: mac ? mac : response.data.mac,
          contract_no: contractNo ? contractNo : response.data.contract_no,
          bef_idx: Number(recorderBefIdx),
        });
        setRecorderEvents((prevData) => ({
          ...prevData,
          user_id: response.data.user_id,
          recorder_mac: mac ? mac : response.data.mac,
        }));
        setOriginMac(response.data.mac);
        setIsInIp(response.data.connect_type === 'IP');
      } else {
        showModal("alert", response.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [recorderBefIdx, recorderIdx, mac, contractNo]);
  
  const [modelList, setModelList] = useState<Model[]>([]);
  
  const getModel = async () => {
    try {
      const response = await getModelAll(0, 0);
      if (response.code === 200) setModelList(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getRecorder();
  },[getRecorder]);
  
  useEffect(() => {
    const handleResize = () => {
      window.scrollTo(0, document.body.scrollHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  return (
    <>
      {loading ? <Loading /> :
        <div className={`${hubLayout.cnt_box}`}>
          <div className={`${hubLayout.acc__cnt}`}>
            <div className={`${hubLayout.sec_title}`}>
              <h2>고객 장비 관리</h2>
              <span onClick={() => navigate(-1)}>X</span>
            </div>
            {!isEvents ?
              <>
                <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`}>장비 정보</h2>
                <div className={`${hubLayout.acc_item_info}`}>
                  <dl>
                    <dt>고객 ID</dt>
                    <dd>
                      <input type="text" className={`${errors.user_id && hubLayout.border_red}`} readOnly
                        placeholder="고객 ID" value={recorder.user_id} 
                        {...register(`user_id`, {
                          required: true,
                        })}
                      />
                    </dd>
                  </dl>
                  <dl>
                    <dt>계약번호</dt>
                    <dd>
                      <input type="text" className={`${errors.contract_no && hubLayout.border_red}`} maxLength={9} readOnly={isPrevCreate}
                        placeholder="계약번호" value={recorder.contract_no} 
                        {...register(`contract_no`, {
                          required: {
                            value: true,
                            message: '계약번호를 입력하세요',
                          },
                          pattern : {
                            value: /^[0-9]{9}$/g,
                            message: '계약번호는 9자리로 입력해주세요.',
                          },
                          onChange: (e:any) => {
                            maxLengthCheck(e);
                            handleToNumericValue(e);
                          },
                        })}
                      />
                      {(errors.contract_no) && 
                        <p className={`${hubLayout.tx_red}`} style={{ fontWeight: 'normal' }}>
                          {errors.contract_no?.message as string}
                        </p>
                      }
                    </dd>
                  </dl>
                  <dl>
                    <dt>MAC</dt>
                    <dd className={`${hubLayout.display_flex}`}>
                      <input type="text" className={`${errors.mac && hubLayout.border_red}`} placeholder="MAC" value={recorder.mac} readOnly={isPrevCreate}
                        {...register(`mac`, {
                          required: {
                            value: true,
                            message: 'MAC을 입력하세요',
                          },
                          pattern : {
                            value: /^([0-9A-F]{2}){5}([0-9A-F]{2})$/g,
                            message: '정확한 MAC을 입력해주세요.',
                          },
                          onChange: handleChange
                        })}
                      />
                      {!isPrevCreate &&
                        <div className={`${hubLayout.width_100}`}>
                          <button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.pink}`} onClick={qrScanner}>QR</button>
                        </div>
                      }
                    </dd>
                  </dl>
                  {!isPrevCreate ?
                    <div className={`${hubLayout.btn_group} ${hubLayout.back_white}`}>
                      <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} disabled={!isValid} onClick={prevHealthCreate}>1차 등록</button>
                    </div>
                    :
                  !isSearch &&
                    <div className={`${hubLayout.btn_group} ${hubLayout.back_white}`}>
                      <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} disabled={!isValid} onClick={prevHealtDelete}>1차 등록 삭제</button>
                      <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} disabled={!isValid} onClick={searchHealth}>Health 조회</button>
                    </div>
                  }
                  {isSearch &&
                    <>
                      <dl>
                        <dt>지사</dt>
                        <dd>
                          <div className={`${hubLayout.inp_select}`}>
                            <select className={`${hubLayout.inp_select}`} value={recorder.jisa_code}
                              {...register(`jisa_code`, {
                                required: {
                                  value: true,
                                  message: '지사를 선택하세요',
                                },
                                onChange: handleChange
                              })} >
                              {jisa.map((item, idx) => (
                                <option key={`jisa_code_${idx}`} value={item.jisa_code}>
                                  {item.jisa_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          {(errors.jisa_code) && 
                            <p className={`${hubLayout.tx_red}`} style={{ fontWeight: 'normal' }}>
                              {errors.jisa_code?.message as string}
                            </p>
                          }
                        </dd>
                      </dl>
                      <dl>
                        <dt>Model</dt>
                        <dd>
                          <div className={`${hubLayout.inp_select}`}>
                            <select className={`${hubLayout.inp_select}`} value={recorder.model || ""}
                              {...register(`model`, {
                                required: {
                                  value: true,
                                  message: 'Model을 선택하세요',
                                },
                                onChange: handleChange
                              })} >
                              {modelList.map((model, idx) => (
                                <option key={`model_${idx}`} value={model.name} disabled={recorder.model !== model.name}>
                                  {model.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          {(errors.model) && 
                            <p className={`${hubLayout.tx_red}`} style={{ fontWeight: 'normal' }}>
                              {errors.model?.message as string}
                            </p>
                          }
                        </dd>
                      </dl>
                      <dl>
                        <dt>설치위치</dt>
                        <dd>
                          <input type="text" className={`${errors.reg_name && hubLayout.border_red}`}
                            placeholder="설치위치" value={recorder.reg_name}
                            {...register(`reg_name`, {
                              required: {
                                value: true,
                                message: '설치위치를 입력하세요',
                              },
                              onChange: handleChange
                            })}
                          />
                          {(errors.reg_name) && 
                            <p className={`${hubLayout.tx_red}`} style={{ fontWeight: 'normal' }}>
                              {errors.reg_name?.message as string}
                            </p>
                          }
                        </dd>
                      </dl>
                      <dl>
                        <dt>매장번호</dt>
                        <dd>
                          <input type="text" className={`${errors.store_key && hubLayout.border_red}`}
                            placeholder="매장번호" value={recorder.store_key}
                            {...register(`store_key`, {
                              required: {
                                value: false,
                                message: '매장번호를 입력하세요. 없을 경우 빈 값.',
                              },
                              onChange: handleChange
                            })}
                          />
                          {(errors.store_key) && 
                            <p className={`${hubLayout.tx_red}`} style={{ fontWeight: 'normal' }}>
                              {errors.store_key?.message as string}
                            </p>
                          }
                        </dd>
                      </dl>
                      <dl>
                        <dt>접속방식</dt>
                        <dd>
                          <span className={`${hubLayout.margin_right_10}`}>
                            <input type="radio" className={`${hubLayout.radio}`} value="DDNS" id='inp-ddns' checked={recorder.connect_type === 'DDNS'}
                              {...register(`connect_type`, {
                                required: true,
                                onChange: handleChange
                              })}
                            />
                            <label htmlFor='inp-ddns'>DDNS</label>
                          </span>
                          <span className={`${hubLayout.margin_right_10}`}>
                            <input type="radio" className={`${hubLayout.radio}`} value="IP" id='inp-ip' checked={recorder.connect_type === 'IP'}
                              {...register(`connect_type`, {
                                required: true,
                                onChange: handleChange
                              })}
                            />
                            <label htmlFor='inp-ip'>고정 IP</label>
                          </span>
                        </dd>
                      </dl>
                      {isInIp &&
                        <>
                          <dl>
                            <dt>내부 IP</dt>
                            <dd>
                              <input type="text" className={`${errors.in_ip1 && hubLayout.border_red}`} style={{ width: '24%', padding: '13px 13px' }} maxLength={3}
                                value={recorder.in_ip && recorder.in_ip.split('.')[0]}
                                {...register(`in_ip1`, {
                                  required: {
                                    value: true,
                                    message: 'IP를 입력하세요.',
                                  },
                                  onChange: (e:any) => {
                                    maxLengthCheck(e);
                                    handleToNumericValue(e);
                                  }
                                })}
                              />
                              .
                              <input type="text" className={`${errors.in_ip2 && hubLayout.border_red}`} style={{ width: '24%', padding: '13px 13px' }} maxLength={3}
                                value={recorder.in_ip && recorder.in_ip.split('.')[1]} 
                                {...register(`in_ip2`, {
                                  required: {
                                    value: true,
                                    message: 'IP를 입력하세요.',
                                  },
                                  onChange: (e:any) => {
                                    maxLengthCheck(e);
                                    handleToNumericValue(e);
                                  }
                                })}
                              />
                              .
                              <input type="text" className={`${errors.in_ip3 && hubLayout.border_red}`} style={{ width: '24%', padding: '13px 13px' }} maxLength={3}
                                value={recorder.in_ip && recorder.in_ip.split('.')[2]} 
                                {...register(`in_ip3`, {
                                  required: {
                                    value: true,
                                    message: 'IP를 입력하세요.',
                                  },
                                  onChange: (e:any) => {
                                    maxLengthCheck(e);
                                    handleToNumericValue(e);
                                  }
                                })}
                              />
                              .
                              <input type="text" className={`${errors.in_ip4 && hubLayout.border_red}`} style={{ width: '24%', padding: '13px 13px' }} maxLength={3}
                                value={recorder.in_ip && recorder.in_ip.split('.')[3]} 
                                {...register(`in_ip4`, {
                                  required: {
                                    value: true,
                                    message: 'IP를 입력하세요.',
                                  },
                                  onChange: (e:any) => {
                                    maxLengthCheck(e);
                                    handleToNumericValue(e);
                                  }
                                })}
                              />
                              {(errors.in_ip1 || errors.in_ip2 || errors.in_ip3 || errors.in_ip4) && 
                                <p className={`${hubLayout.tx_red}`} style={{ fontWeight: 'normal' }}>
                                  {errors?.in_ip1?.message as string || errors?.in_ip2?.message as string || errors?.in_ip3?.message as string || errors?.in_ip4?.message as string}
                                </p>
                              }
                            </dd>
                          </dl>
                          <dl>
                            <dt>내부 PORT</dt>
                            <dd>
                              <input type="text" className={`${errors.in_port && hubLayout.border_red}`}
                                placeholder="내부 PORT" value={recorder.in_port} 
                                {...register(`in_port`, {
                                  required: {
                                    value: true,
                                    message: '내부 PORT를 입력하세요.',
                                  },
                                  onChange: handleToNumericValue
                                })}
                              />
                              {(errors.in_port) && 
                                <p className={`${hubLayout.tx_red}`} style={{ fontWeight: 'normal' }}>
                                  {errors.in_port?.message as string}
                                </p>
                              }
                            </dd>
                          </dl>
                        </>
                      }
                    </>
                  }
                </div>
                <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${hubLayout.back_white}`}>
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} onClick={() => navigate(-1)}>취소</button>
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} disabled={!isValid || !isSearch} onClick={eventsSetting}>다음</button>
                </div>
              </>
            :
              <>
                <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`}>이벤트 설정</h2>
                <div className={`${hubLayout.acc_item_info}`}>
                  <dl>
                    <dt>고객 ID</dt>
                    <dd>
                      <input type="text" className={`${errors.user_id && hubLayout.border_red}`} readOnly
                        placeholder="고객 ID" value={recorder.user_id} 
                        {...register(`user_id`, {
                          required: {
                            value: true,
                            message: '고객 ID를 입력하세요',
                          },
                        })}
                      />
                    </dd>
                  </dl>
                  <dl>
                    <dt>계약번호</dt>
                    <dd>
                      <input type="text" className={`${errors.contract_no && hubLayout.border_red}`} readOnly maxLength={9} placeholder="계약번호" value={recorder.contract_no} />
                    </dd>
                  </dl>
                  <dl>
                    <dt>MAC</dt>
                    <dd className={`${hubLayout.display_flex}`}>
                      <input type="text" className={`${errors.mac && hubLayout.border_red}`} placeholder="MAC" value={recorder.mac} readOnly />
                    </dd>
                  </dl>
                  <div style={{ borderRadius: "0.5rem", border: '1px solid #1234', marginBottom: "1rem" }}>
                    <dl><dt><b>PUSH 설정</b></dt></dl>
                    {modelOne && (() => {
                      const filteredEvents = Object.entries(eventLabels).filter(([key]) => {
                        const eventValue = modelOne[key];
                        const event = recorderEvents[key];
                        return event && typeof event === 'object' && 'push' in event && eventValue === 1;
                      });

                      if (filteredEvents.length === 0) {
                        return <dl><dt>이벤트 없음</dt></dl>;
                      }

                      return filteredEvents.map(([key, label]) => {
                        const event = recorderEvents[key];
                        if (event && typeof event === 'object' && 'push' in event) {
                          return (
                            <dl key={`${key}_push`}>
                              <dt>{label}</dt>
                              <dd className={`${hubLayout.display_flex}`}>
                                {[0, 1, 2, 3].map((val) => (
                                  <span key={val} className={`${hubLayout.margin_right_10}`}>
                                    <input
                                      type="radio"
                                      className={`${hubLayout.radio}`}
                                      value={val}
                                      id={`inp-${key}_push-${val}`}
                                      checked={event.push === val}
                                      onChange={handlePushChange}
                                      name={`${key}_push`}
                                    />
                                    <label htmlFor={`inp-${key}_push-${val}`}>
                                      {["OFF", "경계", "해제", "상시"][val]}
                                    </label>
                                  </span>
                                ))}
                              </dd>
                            </dl>
                          );
                        }
                      });
                    })()}
                  </div>
                  {((contract?.service_code as any).control_service !== 0) &&
                    <div style={{ borderRadius: "0.5rem", border: '1px solid #1234' }}>
                      <dl><dt><b>관제 설정</b></dt></dl>
                      {modelOne && (() => {
                        const filteredEvents = Object.entries(eventLabels).filter(([key]) => {
                          const eventValue = modelOne[key];
                          const event = recorderEvents[key];
                          return event && typeof event === 'object' && 'monitor' in event && eventValue === 1;
                        });

                        if (filteredEvents.length === 0) {
                          return <dl><dt>이벤트 없음</dt></dl>;
                        }

                        return filteredEvents.map(([key, label]) => {
                          const event = recorderEvents[key];
                          if (event && typeof event === 'object') {
                            return (
                              <dl key={`${key}_monitor`}>
                                <dt>{label}</dt>
                                <dd className={`${hubLayout.display_flex}`}>
                                  {[0, 1, 2, 3].map((val) => (
                                    <span key={val} className={`${hubLayout.margin_right_10}`}>
                                      <input
                                        type="radio"
                                        className={`${hubLayout.radio}`}
                                        value={val}
                                        id={`inp-${key}_monitor-${val}`}
                                        checked={event.monitor === val}
                                        onChange={handleMonitorChange}
                                        name={`${key}_monitor`}
                                      />
                                      <label htmlFor={`inp-${key}_monitor-${val}`}>
                                        {["OFF", "경계", "해제", "상시"][val]}
                                      </label>
                                    </span>
                                  ))}
                                </dd>
                              </dl>
                            );
                          }
                        });
                      })()}
                    </div>
                  }
                </div>
                <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${hubLayout.back_white}`}>
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} onClick={() => setIsEvents(prev => !prev)}>이전</button>
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} disabled={!isValid || !isSearch} onClick={_replaceRecorder}>교체</button>
                </div>
              </>
            }
          </div>
        </div>
      }
    </>
  )
}

export default ProductEdit;